import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';

const LanguageContext = createContext();

const STORAGE_KEY = 'dashboard_lang';
// Manual changes are temporary for the current Confirmation session. When the
// iframe is opened again, Daf One's ?lang= value is authoritative and resets
// the session to the language selected in Daf One.
const SESSION_KEY = 'confirmation_session_lang';
const DEFAULT_LANG = 'ar';

const translations = {
  en: {
    'lang.toggle': 'عربي',
    'login.title': 'Sign in to the dashboard',
    'login.email': 'Email address',
    'login.password': 'Password',
    'login.submit': 'Sign in',
    'login.loading': 'Signing in...',
    'login.signupPrompt': "Don't have an account?",
    'login.signupCta': 'Create account',
    'login.or': 'or',
    'login.magicLink.toggle': 'Sign in with a magic link (no password)',
    'login.magicLink.toggleBack': 'Sign in with a password instead',
    'login.magicLink.email': 'Email address',
    'login.magicLink.submit': 'Send me a login link',
    'login.magicLink.sending': 'Sending...',
    'login.magicLink.sent': "✅ We've sent a login link to your email. Open it on this device to sign in.",
    'login.magicLink.error': 'Could not send the link, please try again.',
    'onboarding.title': 'Welcome to Daf Agency!',
    'onboarding.subtitle': 'Complete the details to finish setup',
    'onboarding.shopLabel': 'Shop',
    'onboarding.brandName': 'Brand Name',
    'onboarding.brandName.placeholder': 'Your brand name',
    'onboarding.email': 'Email',
    'onboarding.email.placeholder': 'your@email.com',
    'onboarding.email.hint': 'Use Gmail for the best experience',
    'onboarding.password': 'Password',
    'onboarding.password.placeholder': 'Strong password',
    'onboarding.password.hint': 'At least 6 characters',
    'onboarding.telegramId': 'Telegram ID',
    'onboarding.telegramId.placeholder': '@username or numeric ID',
    'onboarding.telegramId.hint': "Don't know it? Get it from @get_myidrobot",
    'onboarding.facebook': 'Facebook URL (Optional)',
    'onboarding.facebook.placeholder': 'https://facebook.com/your-page',
    'onboarding.instagram': 'Instagram URL (Optional)',
    'onboarding.instagram.placeholder': 'https://instagram.com/your-account',
    'onboarding.messageLanguage': 'Message Language',
    'onboarding.messageLanguage.hint': 'Choose the language for WhatsApp messages sent to your customers',
    'onboarding.messageLanguage.ar': 'AR - عربي',
    'onboarding.messageLanguage.en': 'EN - English',
    'onboarding.submit': 'Finish setup',
    'onboarding.submitting': 'Saving...',
    'onboarding.error.missingFields': 'Please fill in all required fields',
    'onboarding.error.passwordShort': 'Password must be at least 6 characters',
    'onboarding.error.generic': 'An error occurred while completing setup',
    'onboarding.error.missingShop': 'Shop info is missing. Please reinstall the app from Shopify.',
    'onboarding.error.notInstalled': 'You need to install the Order Confirmation app from Shopify first.',
    'billing.title': 'Recharge Plans',
    'billing.subtitle': 'Choose the right plan for you',
    'billing.back': 'Back to dashboard',
    'billing.managedByDafOne.title': 'Your billing is managed by Daf One',
    'billing.managedByDafOne.message': 'Your order confirmation balance is topped up automatically through your Daf One plan. You don\'t need to purchase credits here.',
    'billing.buyNow': 'Buy now',
    'billing.buyWhatsApp': 'Buy via WhatsApp',
    'billing.popular': 'Most Popular',
    'billing.perOrder': 'Order price',
    'billing.monthly': 'Monthly unlimited',
    'billing.ordersLabel': 'Orders',
    'billing.unlimitedMonthly': 'Unlimited / month',
    'billing.feature.unlimitedOrders': 'Unlimited orders',
    'billing.feature.confirmedOrders': '{count} confirmed orders',
    'billing.feature.savePercent': 'Save {discount}% vs standard',
    'billing.feature.instantConfirm': 'Instant confirmation',
    'billing.feature.fastActivation': 'Fast activation',
    'billing.steps.title': 'How to purchase',
    'billing.steps.1': 'Choose a plan',
    'billing.steps.2': 'Click Buy',
    'billing.steps.3': 'Complete payment',
    'billing.steps.4': 'Plan activates automatically',
    'currency.egp': 'EGP',
    'dashboard.logout': 'Log out',
    'dashboard.welcome': 'Welcome,',
    'dashboard.balance': 'Your balance',
    'dashboard.currentPlan': 'Current plan',
    'dashboard.plan.starter': 'Starter',
    'dashboard.plan.basic': 'Basic',
    'dashboard.plan.professional': 'Professional',
    'dashboard.plan.enterprise': 'Enterprise (Unlimited)',
    'dashboard.plan.trial': 'Trial',
    'dashboard.ordersAvailable': 'Available orders',
    'dashboard.orderCost': 'Order cost',
    'dashboard.feedbackCost': 'Feedback message cost',
    'dashboard.buyCredits': 'Buy credits',
    'dashboard.billing.managedByDafOne': 'Your balance is managed by Daf One',
    'dashboard.system.on': 'System active',
    'dashboard.system.off': 'System inactive',
    'dashboard.brandDataTooltip': 'Click to edit profile data',
    'dashboard.editProfile': 'Edit Profile',
    'dashboard.contactUs': 'Contact Us',
    'dashboard.contactUsTooltip': 'Contact us on WhatsApp for support',
    'dashboard.contactUsMessage': 'Hello DAF Agency!\n\nBrand: {brandName}\nPlatform: {platform}\n\nI need help with:',
    'dashboard.telegram.title': 'Enable Telegram notifications',
    'dashboard.telegram.description': 'Turn on the bot to receive:',
    'dashboard.telegram.itemQuestions': '💬 New customer questions',
    'dashboard.telegram.itemOrders': '📦 Order notifications',
    'dashboard.telegram.itemAlerts': '⚠️ Important alerts',
    'dashboard.telegram.cta': 'Open Telegram bot',
    'dashboard.telegram.hint': 'Send /start in the bot to activate notifications',
    'dashboard.savedShipping.title': 'Shipping cost we saved you 💰',
    'dashboard.feedback.title': 'Customer feedback request',
    'dashboard.feedback.toggle.on': '✓ Feedback request enabled',
    'dashboard.feedback.toggle.off': '✕ Feedback request disabled',
    'dashboard.feedback.priceLabel': 'Feedback message price',
    'dashboard.feedback.when.title': '📌 When do we send the feedback message?',
    'dashboard.feedback.when.shopify': '🛍️ Shopify: sent automatically when financial_status becomes paid',
    'dashboard.feedback.when.easyorder': '📦 EasyOrder: sent automatically when status = delivered',
    'dashboard.duplicatedOrderNote.short.on': '✓ Duplicate note',
    'dashboard.duplicatedOrderNote.short.off': '✕ Duplicate note',
    'dashboard.duplicatedOrderNote.tooltip': 'When a customer places a new order while having a pending one, we add a note on both orders. Disable to skip these notes.',
    'dashboard.stats.confirmed': 'Confirmed orders ✅',
    'dashboard.stats.ai': 'AI 🤖',
    'dashboard.stats.cancelled': 'Cancelled orders ❌',
    'dashboard.stats.unclear': 'Unclear orders ❓',
    'dashboard.stats.total': 'Total orders 📦',
    'dashboard.stats.autocancel.on': '✓ Auto-cancel (16h)',
    'dashboard.stats.autocancel.off': '✕ Auto-cancel disabled',
    'dashboard.stats.autocancel.tooltip': 'Changing the timeout (16 / 24 / 72 h) only affects orders created after the change. Orders that already exist will not be affected.',
    'dashboard.firstMsgDelay.button': 'First message timing',
    'dashboard.firstMsgDelay.current': 'Current',
    'dashboard.firstMsgDelay.pick': 'Send first message after:',
    'dashboard.firstMsgDelay.tooltip': 'Choose how long after an order is placed we send the first WhatsApp confirmation message. You can set it from 15 minutes up to 3 days. The change only affects new orders created after it. Default is 1 hour.',
    'dashboard.firstMsgDelay.unit.min': '{n} min',
    'dashboard.firstMsgDelay.unit.hour': '{n} hour',
    'dashboard.firstMsgDelay.unit.hours': '{n} hours',
    'dashboard.firstMsgDelay.unit.day': '1 day',
    'dashboard.firstMsgDelay.unit.days': '{n} days',
    'alerts.firstMsgDelay.saved': '✅ First message timing updated to: {label}',
    'dashboard.deliveryDate.toggle.on': '✓ Expected delivery date',
    'dashboard.deliveryDate.toggle.off': '✕ Expected delivery date',
    'dashboard.deliveryDate.daysLabel': 'Avg. delivery days',
    'dashboard.deliveryDate.tooltip': 'When enabled, we include an expected delivery date in the first confirmation message. Date = send time + your average days.',
    'alerts.deliveryDate.enabled': '✅ Expected delivery date enabled',
    'alerts.deliveryDate.disabled': '❌ Expected delivery date disabled',
    'alerts.deliveryDate.daysSaved': '✅ Average delivery days saved: {n} days',
    'dashboard.aiMode.on': '🤖 AI On',
    'dashboard.aiMode.off': '🔕 AI Off',
    'dashboard.aiMode.tooltip': 'When AI is OFF: only order confirmation works. Customer questions are redirected to your Facebook or Instagram page.',
    'alerts.aiMode.enabled': '✅ AI mode is now ON. The AI will handle conversations and customer questions.',
    'alerts.aiMode.disabled': '⏸️ AI mode is OFF. Only order confirmation is active. Customer questions go to your social page.',
    'dashboard.shippingCheck.on': '📍 Address Check On',
    'dashboard.shippingCheck.off': '📍 Address Check Off',
    'dashboard.shippingCheck.tooltip': 'When enabled, the AI will verify shipping addresses with customers after order confirmation. Only works when AI is ON.',
    'alerts.shippingCheck.enabled': '✅ Address check is now ON. The AI will verify shipping addresses with customers.',
    'alerts.shippingCheck.disabled': '⏸️ Address check is OFF.',
    'dashboard.orders.heading': 'Orders',
    'dashboard.orders.countLabel': 'Count',
    'dashboard.orders.csv': 'Download CSV',
    'dashboard.orders.searchOrder': '🔍 Search by order number...',
    'dashboard.orders.searchPhone': '📱 Search by phone...',
    // G11.2.b — name search: asked for by name, never existed before
    'dashboard.orders.searchName': '👤 Search by customer name...',
    // G12.2 — the list ends; say so rather than leaving it ambiguous
    'dashboard.orders.endOfList': 'that\'s all of them',
    'dashboard.orders.clearSearch': 'Clear search ✕',
    'dashboard.orders.table.chatMode': 'Chat mode',
    'dashboard.orders.table.message': 'Message',
    'dashboard.orders.table.products': 'Products',
    'dashboard.orders.table.price': 'Price',
    'dashboard.orders.table.phone': 'Phone',
    'dashboard.orders.table.customer': 'Customer name',
    'dashboard.orders.table.created': 'Created at',
    'dashboard.orders.table.orderNumber': 'Order number',
    'dashboard.orders.table.orderId': 'Order ID',
    'dashboard.orders.table.status': 'Final status',
    'dashboard.orders.loading': 'Loading orders...',
    'dashboard.orders.empty': 'No orders to show (current filter).',
    'dashboard.orders.loadMore': '📥 Load more orders',
    'dashboard.orders.loadingMore': '⏳ Loading more...',
    'dashboard.orders.filters.all': 'All Orders',
    'dashboard.orders.filters.today': 'Today',
    'dashboard.orders.filters.yesterday': 'Yesterday',
    'dashboard.orders.filters.dayBefore': 'Day Before',
    'dashboard.orders.filters.from': 'From:',
    'dashboard.orders.filters.to': 'To:',
    'dashboard.pending.title': '⏳ Pending Orders',
    'dashboard.pending.hint': 'These are orders that are not confirmed or cancelled yet — need follow-up.',
    'dashboard.pending.export': '📥 Export Excel',
    'dashboard.pending.searchOrder': '🔍 Search by order number...',
    'dashboard.pending.searchPhone': '📱 Search by phone...',
    'dashboard.pending.clearSearch': 'Clear search ✕',
    'dashboard.pending.table.status': 'Status',
    'dashboard.pending.table.message': 'Message',
    'dashboard.pending.table.products': 'Products',
    'dashboard.pending.table.price': 'Price',
    'dashboard.pending.table.phone': 'Phone',
    'dashboard.pending.table.customer': 'Customer name',
    'dashboard.pending.table.created': 'Created at',
    'dashboard.pending.table.orderNumber': 'Order number',
    'dashboard.pending.table.orderId': 'Order ID',
    'dashboard.pending.table.call': 'Call customer',
    'dashboard.pending.table.whatsapp': 'WhatsApp',
    'dashboard.pending.table.confirm': 'Confirm order',
    'dashboard.pending.table.cancel': 'Cancel order',
    'dashboard.pending.table.delete': 'Delete',
    'dashboard.pending.empty': 'No pending orders.',
    'dashboard.pending.loading': 'Loading pending orders...',
    'dashboard.nonAi.toggle.ai': '🤖 AI',
    'dashboard.nonAi.toggle.manual': '👤 Manual',
    'dashboard.pending.emptySearch': 'No pending orders match the search.',
    'dashboard.pending.emptyAll': 'No pending orders — all clear! ✅',
    'dashboard.manual.title': '👤 Manual conversations',
    'dashboard.manual.description': 'Customers you chose to handle manually (AI is off).',
    'dashboard.manual.emptyTitle': '🤖 All conversations are handled by AI right now',
    'dashboard.manual.emptyHint': 'To handle a customer manually, click the chat mode button in any table.',
    'dashboard.manual.searchPlaceholder': '🔍 Search by phone...',
    'dashboard.manual.actions': 'Actions',
    'dashboard.manual.addedAt': 'Added at',
    'dashboard.manual.customerPhone': 'Customer phone',
    'dashboard.manual.lastMessage': 'Last message',
    'dashboard.manual.reenableAi': '🤖 Enable AI',
    'dashboard.manual.lastMessage.customer': 'Customer: ',
    'dashboard.manual.lastMessage.bot': 'Bot: ',
    'dashboard.manual.lastMessage.none': '-',
    'dashboard.leads.title': 'Latest chats (Leads)',
    'dashboard.leads.description': 'All chats (even without orders). Click any row to open chat.',
    'dashboard.leads.searchPhone': '🔍 Search by customer phone...',
    'dashboard.leads.searchOrder': '🔍 Search by order number...',
    'dashboard.leads.clearSearch': 'Clear search ✕',
    'dashboard.leads.table.chatMode': 'Chat mode',
    'dashboard.leads.table.lastUpdated': 'Last updated',
    'dashboard.leads.table.lastMessage': 'Last message',
    'dashboard.leads.table.orderNumber': 'Order number',
    'dashboard.leads.table.customerPhone': 'Customer phone',
    'dashboard.leads.table.status': 'Status',
    'dashboard.leads.loading': 'Loading chats...',
    'dashboard.leads.emptySearch': 'No chats match the search.',
    'dashboard.leads.empty': 'No chats to display.',
    'dashboard.leads.loadMore': '📥 Load more chats',
    'dashboard.leads.loadingMore': '⏳ Loading more...',
    'dashboard.leads.hintLoadMore': '💡 Not finding what you need? Click "Load more" to fetch older records.',
    'feedbacks.title': '⭐ Customer feedbacks',
    'feedbacks.description': 'Customer ratings and issues after delivery — click any row to view details and reply.',
    'feedbacks.searchPlaceholder': '🔍 Search by customer phone...',
    'feedbacks.clearSearch': 'Clear search ✕',
    'feedbacks.table.chatMode': 'Chat mode',
    'feedbacks.table.date': 'Date',
    'feedbacks.table.status': 'Status',
    'feedbacks.table.comment': 'Comment',
    'feedbacks.table.customerPhone': 'Customer phone',
    'feedbacks.table.brandName': 'Brand name',
    'feedbacks.loading': 'Loading feedbacks...',
    'feedbacks.emptySearch': 'No feedbacks match the search.',
    'feedbacks.empty': 'No feedbacks to display.',
    'feedbacks.loadMore': '📥 Load more feedbacks',
    'feedbacks.loadingMore': '⏳ Loading more...',
    'bosta.card.title': '📦 Bosta',
    'bosta.card.active': '🟢 Active',
    'bosta.card.inactive': '⚪ Inactive',
    'bosta.card.actionsActive': 'Bosta actions active',
    'bosta.card.actionsStopped': 'Bosta actions stopped',
    'bosta.card.addApiKeyFirst': '⚠️ Add API Key first',
    'bosta.card.noApiKeyAlert': '⚠️ You must add the API Key first from the Bosta settings page',
    'bosta.card.priceLabel': 'Bosta message price',
    'bosta.toggle.enabled': '✅ Bosta actions enabled!',
    'bosta.toggle.disabled': '❌ Bosta actions disabled.',
    'bosta.table.title': '📦 Bosta Shipments',
    'bosta.table.description': 'Bosta shipment updates and delivery status — click any row to view details and reply.',
    'bosta.table.searchPlaceholder': '🔍 Search by customer phone...',
    'bosta.table.clearSearch': 'Clear search ✕',
    'bosta.table.chatMode': 'Chat mode',
    'bosta.table.date': 'Date',
    'bosta.table.status': 'Status',
    'bosta.table.comment': 'Comment',
    'bosta.table.customerPhone': 'Customer phone',
    'bosta.table.brandName': 'Brand name',
    'bosta.table.loading': 'Loading bosta data...',
    'bosta.table.emptySearch': 'No bosta records match the search.',
    'bosta.table.empty': 'No bosta records to display.',
    'bosta.table.loadMore': '📥 Load more bosta records',
    'bosta.table.loadingMore': '⏳ Loading more...',
    'bosta.page.title': '⚙️ Bosta Settings',
    'bosta.page.webhookTitle': '📡 Webhook URL',
    'bosta.page.webhookDescription': 'Use this Webhook URL in your Bosta settings:',
    'bosta.page.copyLink': '📋 Copy link',
    'bosta.page.apiKeyTitle': '🔑 Bosta API Key',
    'bosta.page.apiKeyDescription': 'Enter your Bosta account API Key:',
    'bosta.page.apiKeyPlaceholder': 'Enter Bosta API Key',
    'bosta.page.saving': '⏳ Saving...',
    'bosta.page.save': '💾 Save',
    'bosta.page.noteTitle': 'ℹ️ Note:',
    'bosta.page.note1': 'Make sure to enable the Webhooks feature in your Bosta account',
    'bosta.page.note2': 'You must add the Webhook URL in Bosta settings',
    'bosta.page.note3': 'API Key is required to communicate with the Bosta service',
    'bosta.page.apiKeyRequired': '⚠️ Please enter the API Key',
    'bosta.page.saveSuccess': '✅ API Key saved successfully!',
    'bosta.page.saveError': '❌ An error occurred while saving',
    'bosta.page.webhookCopied': '✅ Webhook link copied!',
    'bosta.page.clientNotFound': 'Client data not found',
    'bosta.page.loadError': 'An error occurred while loading data',
    'bosta.page.keyConfigured': '✅ An API Key is already configured',
    'bosta.page.keyNotConfigured': '⚠️ No API Key configured yet',
    'kb.title': 'Knowledge base (auto replies)',
    'kb.loading': 'Loading knowledge base...',
    'kb.empty': 'Knowledge base is empty for now.',
    'kb.table.action': 'Action',
    'kb.table.autoAnswer': 'Auto answer',
    'kb.table.question': 'Saved question',
    'kb.table.context': 'Context (product)',
    'kb.table.addedAt': 'Added at',
    'kb.button.edit': 'Edit ✏️',
    'kb.button.delete': 'Delete 🗑️',
    'kb.context.default': 'General question',
    'questions.title': 'Customer questions (need reply)',
    'questions.loading': 'Loading questions...',
    'questions.empty': 'No new questions need replies. 10/10',
    'questions.table.action': 'Action',
    'questions.table.text': 'Question text',
    'questions.table.product': 'Product (context)',
    'questions.table.order': 'Order number',
    'questions.table.phone': 'Customer phone',
    'questions.table.date': 'Question date',
    'questions.button.reply': 'Reply 💬',
    'questions.button.delete': 'Delete 🗑️',
    'questions.modal.answerTitle': 'Reply to customer question',
    'questions.modal.customerQuestion': 'Customer question:',
    'questions.modal.productContext': 'About product:',
    'questions.modal.placeholder': 'Write your reply here...',
    'questions.modal.saveToKb': 'Save this answer to the knowledge base (for future auto replies)',
    'questions.modal.cancel': 'Cancel',
    'questions.modal.send': 'Send reply 🚀',
    'questions.modal.sending': 'Sending...',
    'questions.modal.editTitle': 'Edit auto answer',
    'questions.modal.savedQuestion': 'Saved question:',
    'questions.modal.autoAnswerLabel': 'Auto answer:',
    'questions.modal.editPlaceholder': 'Write the updated answer here...',
    'questions.modal.saveEdit': 'Save changes ✅',
    'questions.modal.savingEdit': 'Saving...',
    'clientInfo.title': '📋 Client info',
    'clientInfo.brandName': 'Brand name:',
    'clientInfo.clientId': 'Client ID:',
    'clientInfo.email': 'Email:',
    'clientInfo.platform': 'Platform type:',
    'clientInfo.shopDomain': 'Shop domain:',
    'clientInfo.platform.shopify': '🛍️ Shopify',
    'clientInfo.platform.easyorder': '📦 EasyOrder',
    'clientInfo.platform.default': 'General',
    'clientInfo.shopDomain.fallback': 'Not set',
    'clientInfo.telegram.placeholder': 'e.g. @username or 123456789',
    'clientInfo.telegram.getIdHint': 'Get your User ID from the bot:',
    'clientInfo.facebook.placeholder': 'e.g. https://facebook.com/yourpage',
    'clientInfo.instagram.placeholder': 'e.g. https://instagram.com/yourpage',
    'clientInfo.close': 'Close',
    'clientInfo.save': '💾 Save changes',
    'clientInfo.saving': '⏳ Saving...',
    'operationalSetup.banner.title': 'Finish setting up your account',
    'operationalSetup.banner.body': "You're all set up via Daf One — just add your Telegram admin ID so we can notify you about orders.",
    'operationalSetup.banner.cta': 'Finish now',
    'operationalSetup.modal.title': '🚀 Finish setting up',
    'operationalSetup.modal.subtitle': 'One quick step so we can keep you posted on WhatsApp/Telegram.',
    'operationalSetup.modal.telegramRequired': 'Telegram ID (required)',
    'operationalSetup.modal.save': '✅ Save and continue',
    'operationalSetup.modal.saving': '⏳ Saving...',
    'operationalSetup.modal.close': 'Later',
    'operationalSetup.modal.telegramError': 'Please add your Telegram ID first.',
    'operationalSetup.modal.saveError': 'Something went wrong saving your info, please try again.',
    'operationalSetup.modal.messageLanguage': 'Message language',
    'operationalSetup.modal.messageLanguage.hint': 'The language we use for WhatsApp messages to your customers',
    'chat.modal.title': 'Customer chat history',
    'chat.modal.switchToManual': '👤 Switch to human',
    'chat.modal.switchingToManual': 'Switching...',
    'chat.modal.manualActive': '👤 Human mode is on — AI replies are paused',
    'chat.modal.modeLoading': 'Checking chat mode...',
    'chat.modal.modeUnavailable': 'Chat mode unavailable',
    'chat.modal.customerFallback': 'Customer',
    'chat.modal.loading': 'Loading chat history...',
    'chat.modal.empty': 'No chat history for this customer.',
    'chat.modal.send': '📤 Send',
    'chat.modal.sending': '...',
    'chat.list.conversation': 'Conversation',
    'chat.list.emptyConversation': 'Empty conversation',
    'alerts.saveInfo.success': '✅ Data updated successfully!',
    'alerts.saveInfo.noChanges': '⚠️ No new data entered',
    'alerts.saveInfo.error': '❌ An error occurred while updating',
    'alerts.answer.success': '✅ Reply sent to the customer successfully',
    'alerts.answer.savedToKb': '📚 Saved to knowledge base',
    'alerts.answer.error': '❌ Error sending reply:',
    'alerts.question.deleteConfirm': 'Are you sure you want to delete this question permanently? This action cannot be undone.',
    'alerts.question.deleteError': 'Error deleting question. Please try again.',
    'alerts.kb.saveSuccess': 'Changes saved successfully!',
    'alerts.kb.saveError': 'Error saving changes.',
    'alerts.kb.deleteConfirm': 'Are you sure you want to delete this auto reply? It will be removed from the knowledge base.',
    'alerts.kb.deleteError': 'Error deleting item. Please try again.',
    'alerts.pending.none': 'No pending orders to export',
    'alerts.export.error': 'An error occurred while exporting data',
    'alerts.autoCancel.enabled': '✅ Auto-cancel enabled! Orders with no WhatsApp reply will be canceled after 16 hours.',
    'alerts.autoCancel.disabled': '❌ Auto-cancel disabled. Orders will not be canceled automatically.',
    'alerts.duplicatedOrderNote.enabled': '✅ Duplicated order note enabled. We will add a note on both the old and the new order.',
    'alerts.duplicatedOrderNote.disabled': '❌ Duplicated order note disabled. No notes will be added on duplicated orders.',
    'alerts.settingError': 'Error updating setting. Please try again.',
    'alerts.feedbackToggle.enabled': '✅ Feedback requests enabled! Customers will be asked for ratings after delivery.',
    'alerts.feedbackToggle.disabled': '❌ Feedback requests disabled.',
    'alerts.feedback.incomplete': 'Feedback data is incomplete.',
    'alerts.chat.followError': 'Error while following the conversation.',
    'alerts.chat.openError': 'Error opening the conversation.',
    'alerts.chat.dataIncomplete': 'Customer data is incomplete to show the chat.',
    'alerts.chat.toggleError': 'Error while changing chat mode.',
    'alerts.message.sentWithTime': '✅ Message sent successfully!\n\n⏰ {time}',
    'alerts.message.sent': '✅ Message sent successfully!',
    'alerts.message.errorPrefix': '❌',
    'alerts.message.sendError': 'An error occurred while sending the message',
    'alerts.toggle.incompleteData': 'Incomplete data',
    'loading.app': 'Loading...',
    'loading.shopifyCheck': 'Checking your Shopify account...',
    'loading.brandData': 'Loading brand data...',
    'loading.noData': 'No data...',
    'loading.magicLink': 'Signing you in...',
    'loading.autoProvision': "We're setting up your account...",
    'error.clientNotFound': 'Client data not found',
    'error.fetchFailed': 'Failed to load client data',
    'error.logout': 'Log out'
  },
  ar: {
    'lang.toggle': 'English',
    'login.title': 'تسجيل الدخول للوحة التحكم',
    'login.email': 'البريد الإلكتروني',
    'login.password': 'كلمة المرور',
    'login.submit': 'تسجيل الدخول',
    'login.loading': 'جاري الدخول...',
    'login.signupPrompt': 'ليس لديك حساب؟',
    'login.signupCta': 'أنشئ حساب جديد',
    'login.or': 'أو',
    'login.magicLink.toggle': 'الدخول برابط سحري (من غير باسورد)',
    'login.magicLink.toggleBack': 'الدخول بالباسورد بدل كده',
    'login.magicLink.email': 'البريد الإلكتروني',
    'login.magicLink.submit': 'ابعتلي رابط الدخول',
    'login.magicLink.sending': 'جاري الإرسال...',
    'login.magicLink.sent': '✅ بعتنالك رابط دخول على إيميلك. افتحه من نفس الجهاز ده عشان تدخل.',
    'login.magicLink.error': 'حصل خطأ في إرسال الرابط، حاول تاني.',
    'onboarding.title': 'مرحبا في Daf Agency!',
    'onboarding.subtitle': 'أكمل البيانات التالية لإنهاء الإعداد',
    'onboarding.shopLabel': 'المتجر',
    'onboarding.brandName': 'اسم البراند',
    'onboarding.brandName.placeholder': 'اسم علامتك التجارية',
    'onboarding.email': 'الإيميل',
    'onboarding.email.placeholder': 'your@email.com',
    'onboarding.email.hint': 'استخدم Gmail للحصول على أفضل تجربة',
    'onboarding.password': 'كلمة المرور',
    'onboarding.password.placeholder': 'كلمة مرور قوية',
    'onboarding.password.hint': 'على الأقل 6 أحرف',
    'onboarding.telegramId': 'Telegram ID',
    'onboarding.telegramId.placeholder': '@username أو رقم المعرّف',
    'onboarding.telegramId.hint': 'لا تعرف معرفك؟ احصل عليه من @get_myidrobot',
    'onboarding.facebook': 'رابط فيسبوك (اختياري)',
    'onboarding.facebook.placeholder': 'https://facebook.com/your-page',
    'onboarding.instagram': 'رابط إنستجرام (اختياري)',
    'onboarding.instagram.placeholder': 'https://instagram.com/your-account',
    'onboarding.messageLanguage': 'لغة الرسالة',
    'onboarding.messageLanguage.hint': 'اختر لغة رسائل الواتساب المرسلة لعملائك',
    'onboarding.messageLanguage.ar': 'AR - عربي',
    'onboarding.messageLanguage.en': 'EN - English',
    'onboarding.submit': 'إكمال الإعداد',
    'onboarding.submitting': 'جاري الحفظ...',
    'onboarding.error.missingFields': 'الرجاء ملء جميع الحقول المطلوبة',
    'onboarding.error.passwordShort': 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
    'onboarding.error.generic': 'حدث خطأ أثناء إكمال الإعداد',
    'onboarding.error.missingShop': 'معلومات المتجر غير موجودة. الرجاء إعادة تثبيت التطبيق من Shopify.',
    'onboarding.error.notInstalled': 'محتاج تثبّت تطبيق تأكيد الأوردرات الأول من Shopify.',
    'billing.title': 'باقات شحن الرصيد',
    'billing.subtitle': 'اختر الباقة المناسبة لك',
    'billing.back': '← العودة للداشبورد',
    'billing.managedByDafOne.title': 'فوترتك مُدارة من Daf One',
    'billing.managedByDafOne.message': 'رصيد تأكيد الأوردرات بتاعك بيتشحن أوتوماتيك من خلال باقتك في Daf One. مش محتاج تشتري رصيد من هنا.',
    'billing.buyNow': 'اشتري الآن',
    'billing.buyWhatsApp': 'اشتري الآن عبر واتساب',
    'billing.popular': 'الأكثر شعبية',
    'billing.perOrder': 'سعر الأوردر',
    'billing.monthly': 'باقة شهرية غير محدودة',
    'billing.ordersLabel': 'أوردر',
    'billing.unlimitedMonthly': 'غير محدود / شهر',
    'billing.feature.unlimitedOrders': 'أوردرات غير محدودة',
    'billing.feature.confirmedOrders': '{count} طلب مؤكد',
    'billing.feature.savePercent': 'وفر {discount}% عن السعر العادي',
    'billing.feature.instantConfirm': 'تأكيد فوري',
    'billing.feature.fastActivation': 'تفعيل سريع',
    'billing.steps.title': 'طريقة الشراء',
    'billing.steps.1': 'اختر الباقة المناسبة',
    'billing.steps.2': 'اضغط على زر "اشتري الآن"',
    'billing.steps.3': 'أكمل عملية الدفع',
    'billing.steps.4': 'سيتم تفعيل الباقة تلقائياً',
    'currency.egp': 'ج.م',
    'dashboard.logout': 'تسجيل خروج',
    'dashboard.welcome': 'أهلاً بك،',
    'dashboard.balance': 'رصيدك الحالي',
    'dashboard.currentPlan': 'الباقة الحالية',
    'dashboard.plan.starter': 'باقة البداية',
    'dashboard.plan.basic': 'الباقة الأساسية',
    'dashboard.plan.professional': 'الباقة الاحترافية',
    'dashboard.plan.enterprise': 'باقة المؤسسات (غير محدود)',
    'dashboard.plan.trial': 'تجريبي',
    'dashboard.ordersAvailable': 'الأوردرات المتاحة',
    'dashboard.orderCost': 'تكلفة الأوردر',
    'dashboard.feedbackCost': 'تكلفة رسالة التقييم',
    'dashboard.buyCredits': 'اشحن رصيدك',
    'dashboard.billing.managedByDafOne': 'رصيدك مُدار من Daf One',
    'dashboard.system.on': 'النظام مُفعّل',
    'dashboard.system.off': 'النظام مُعطّل',
    'dashboard.brandDataTooltip': 'اضغط لتعديل البيانات',
    'dashboard.editProfile': 'تعديل البيانات',
    'dashboard.contactUs': 'تواصل معنا',
    'dashboard.contactUsTooltip': 'تواصل معانا على واتساب للدعم',
    'dashboard.contactUsMessage': 'مرحباً فريق DAF!\n\nالبراند: {brandName}\nالمنصة: {platform}\n\nمحتاج مساعدة في:',
    'dashboard.telegram.title': 'تفعيل إشعارات التليجرام',
    'dashboard.telegram.description': 'فعّل البوت عشان تستقبل:',
    'dashboard.telegram.itemQuestions': '💬 أسئلة العملاء الجديدة',
    'dashboard.telegram.itemOrders': '📦 إشعارات الطلبات',
    'dashboard.telegram.itemAlerts': '⚠️ تنبيهات مهمة',
    'dashboard.telegram.cta': 'افتح بوت التليجرام',
    'dashboard.telegram.hint': 'اضغط /start في البوت لتفعيل الإشعارات',
    'dashboard.savedShipping.title': 'تكلفة الشحن اللي وفرتهالك 💰',
    'dashboard.feedback.title': 'طلب تقييم العملاء',
    'dashboard.feedback.toggle.on': '✓ طلب التقييم مفعّل',
    'dashboard.feedback.toggle.off': '✕ طلب التقييم معطل',
    'dashboard.feedback.priceLabel': 'سعر رسالة الـ Feedback',
    'dashboard.feedback.when.title': '📌 متى يتم إرسال رسالة التقييم؟',
    'dashboard.feedback.when.shopify': '🛍️ Shopify: يتم إرسال الرسالة تلقائياً عندما تتحول financial_status إلى paid',
    'dashboard.feedback.when.easyorder': '📦 EasyOrder: يتم إرسال الرسالة تلقائياً عندما تصبح حالة الطلب status = delivered (تم التوصيل)',
    'dashboard.duplicatedOrderNote.short.on': '✓ نوته متكرر',
    'dashboard.duplicatedOrderNote.short.off': '✕ نوته متكرر',
    'dashboard.duplicatedOrderNote.tooltip': 'لما عميل يعمل أوردر جديد وعنده أوردر تاني لسه pending، بنحط نوته على الأوردر القديم والجديد. اقفل الزرار لو مش عايز يتحط أي نوته.',
    'dashboard.stats.confirmed': 'أوردرات مؤكدة ✅',
    'dashboard.stats.ai': 'AI 🤖',
    'dashboard.stats.cancelled': 'أوردرات ملغاة ❌',
    'dashboard.stats.unclear': 'أوردرات (غير واضحة) ❓',
    'dashboard.stats.total': 'إجمالي الأوردرات 📦',
    'dashboard.stats.autocancel.on': '✓ إلغاء تلقائي (16 ساعة)',
    'dashboard.stats.autocancel.off': '✕ إلغاء تلقائي معطل',
    'dashboard.stats.autocancel.tooltip': 'تغيير الوقت (16 / 24 / 72 ساعة) بيأثر على الأوردرات الجديدة بس اللي هتيجي بعد التغيير. الأوردرات الموجودة قبل كده مش هتتأثر.',
    'dashboard.firstMsgDelay.button': 'توقيت أول رسالة',
    'dashboard.firstMsgDelay.current': 'الحالي',
    'dashboard.firstMsgDelay.pick': 'ابعت أول رسالة بعد:',
    'dashboard.firstMsgDelay.tooltip': 'اختار بعد قد إيه من عمل الأوردر نبعت أول رسالة تأكيد على الواتساب. تقدر تظبطها من ربع ساعة لحد 3 أيام. التغيير بيأثر على الأوردرات الجديدة بس اللي بعده. الافتراضي ساعة واحدة.',
    'dashboard.firstMsgDelay.unit.min': '{n} دقيقة',
    'dashboard.firstMsgDelay.unit.hour': 'ساعة',
    'dashboard.firstMsgDelay.unit.hours': '{n} ساعة',
    'dashboard.firstMsgDelay.unit.day': 'يوم',
    'dashboard.firstMsgDelay.unit.days': '{n} أيام',
    'alerts.firstMsgDelay.saved': '✅ تم تحديث توقيت أول رسالة إلى: {label}',
    'dashboard.deliveryDate.toggle.on': '✓ موعد تسليم متوقع',
    'dashboard.deliveryDate.toggle.off': '✕ موعد تسليم متوقع',
    'dashboard.deliveryDate.daysLabel': 'متوسط أيام التوصيل',
    'dashboard.deliveryDate.tooltip': 'لما تفعّل الخاصية دي، بنضيف موعد تسليم متوقع في رسالة التأكيد الأولى. الموعد = وقت الإرسال + عدد الأيام اللي تحددها.',
    'alerts.deliveryDate.enabled': '✅ تم تفعيل موعد التسليم المتوقع',
    'alerts.deliveryDate.disabled': '❌ تم تعطيل موعد التسليم المتوقع',
    'alerts.deliveryDate.daysSaved': '✅ تم حفظ متوسط أيام التوصيل: {n} أيام',
    'dashboard.aiMode.on': '🤖 AI شغال',
    'dashboard.aiMode.off': '🔕 AI واقف',
    'dashboard.aiMode.tooltip': 'لو أوقفت الـ AI: النظام هيشتغل بس في تأكيد الأوردرات. أي استفسار من العميل هيتبعت للصفحة على فيسبوك أو إنستا.',
    'alerts.aiMode.enabled': '✅ الـ AI اتفعّل. هيرد على العملاء ويتعامل مع أسئلتهم.',
    'alerts.aiMode.disabled': '⏸️ الـ AI اتوقف. النظام شغال بس في تأكيد الأوردرات. الاستفسارات هتتبعت لصفحتك.',
    'dashboard.shippingCheck.on': '📍 فحص العنوان شغال',
    'dashboard.shippingCheck.off': '📍 فحص العنوان واقف',
    'dashboard.shippingCheck.tooltip': 'لما يكون شغال، الـ AI هيأكد مع العميل على عنوان الشحن بعد تأكيد الأوردر. بيشتغل بس لما الـ AI يكون مفتوح.',
    'alerts.shippingCheck.enabled': '✅ فحص العنوان اتفعّل. الـ AI هيأكد العناوين مع العملاء.',
    'alerts.shippingCheck.disabled': '⏸️ فحص العنوان اتوقف.',
    'dashboard.orders.heading': 'الأوردرات',
    'dashboard.orders.countLabel': 'العدد',
    'dashboard.orders.csv': 'تحميل كـ CSV',
    'dashboard.orders.searchOrder': '🔍 ابحث برقم الأوردر...',
    'dashboard.orders.searchPhone': '📱 ابحث برقم التليفون...',
    // G11.2.b — البحث بالاسم
    'dashboard.orders.searchName': '👤 ابحث باسم العميل...',
    // G12.2 — قول إن الليستة خلصت بدل ما تسيبها مبهمة
    'dashboard.orders.endOfList': 'خلصوا كلهم',
    'dashboard.orders.clearSearch': 'مسح البحث ✕',
    'dashboard.orders.table.chatMode': 'وضع الشات',
    'dashboard.orders.table.message': 'الرسالة',
    'dashboard.orders.table.products': 'المنتجات',
    'dashboard.orders.table.price': 'السعر',
    'dashboard.orders.table.phone': 'التليفون',
    'dashboard.orders.table.customer': 'اسم العميل',
    'dashboard.orders.table.created': 'وقت الإنشاء',
    'dashboard.orders.table.orderNumber': 'رقم الأوردر',
    'dashboard.orders.table.orderId': 'Order ID',
    'dashboard.orders.table.status': 'الحالة النهائية',
    'dashboard.orders.loading': 'جاري تحميل الأوردرات...',
    'dashboard.orders.empty': 'لا يوجد أوردرات لعرضها (حسب الفلتر الحالي).',
    'dashboard.orders.loadMore': '📥 تحميل المزيد من الطلبات',
    'dashboard.orders.loadingMore': '⏳ جاري التحميل...',
    'dashboard.orders.filters.all': 'All Orders',
    'dashboard.orders.filters.today': 'Today',
    'dashboard.orders.filters.yesterday': 'Yesterday',
    'dashboard.orders.filters.dayBefore': 'Day Before',
    'dashboard.orders.filters.from': 'From:',
    'dashboard.orders.filters.to': 'To:',
    'dashboard.pending.title': '⏳ الأوردرات المعلقة (Pending Orders)',
    'dashboard.pending.hint': 'هذه أوردرات لم يتم تأكيدها أو إلغاؤها بعد - تحتاج متابعة.',
    'dashboard.pending.export': '📥 تصدير Excel',
    'dashboard.pending.searchOrder': '🔍 ابحث برقم الأوردر...',
    'dashboard.pending.searchPhone': '📱 ابحث برقم التليفون...',
    'dashboard.pending.clearSearch': 'مسح البحث ✕',
    'dashboard.pending.table.status': 'الحالة',
    'dashboard.pending.table.message': 'الرسالة',
    'dashboard.pending.table.products': 'المنتجات',
    'dashboard.pending.table.price': 'السعر',
    'dashboard.pending.table.phone': 'التليفون',
    'dashboard.pending.table.customer': 'اسم العميل',
    'dashboard.pending.table.created': 'وقت الإنشاء',
    'dashboard.pending.table.orderNumber': 'رقم الأوردر',
    'dashboard.pending.table.orderId': 'Order ID',
    'dashboard.pending.table.call': 'اتصل بالعميل',
    'dashboard.pending.table.whatsapp': 'واتساب',
    'dashboard.pending.table.confirm': 'تأكيد الأوردر',
    'dashboard.pending.table.cancel': 'إلغاء الأوردر',
    'dashboard.pending.table.delete': 'حذف',
    'dashboard.pending.empty': 'لا يوجد أوردرات معلقة.',
    'dashboard.pending.loading': 'جاري تحميل الأوردرات المعلقة...',
    'dashboard.nonAi.toggle.ai': '🤖 AI',
    'dashboard.nonAi.toggle.manual': '👤 Manual',
    'dashboard.pending.emptySearch': 'لا توجد أوردرات معلقة تطابق البحث',
    'dashboard.pending.emptyAll': 'لا توجد أوردرات معلقة - كل شيء تمام! ✅',
    'dashboard.manual.title': '👤 المحادثات اليدوية',
    'dashboard.manual.description': 'العملاء اللي قررت تتابعهم يدوياً (مش الـ AI اللي بيرد عليهم)',
    'dashboard.manual.emptyTitle': '🤖 كل المحادثات بيتابعها الـ AI حالياً',
    'dashboard.manual.emptyHint': 'لو عايز تتابع عميل يدوياً، اضغط على زرار "وضع الشات" في أي جدول',
    'dashboard.manual.searchPlaceholder': '🔍 ابحث برقم التليفون...',
    'dashboard.manual.actions': 'الإجراءات',
    'dashboard.manual.addedAt': 'تاريخ الإضافة',
    'dashboard.manual.customerPhone': 'رقم العميل',
    'dashboard.manual.lastMessage': 'آخر رسالة',
    'dashboard.manual.reenableAi': '🤖 تفعيل AI',
    'dashboard.manual.lastMessage.customer': 'العميل: ',
    'dashboard.manual.lastMessage.bot': 'البوت: ',
    'dashboard.manual.lastMessage.none': '-',
    'dashboard.leads.title': 'أحدث المحادثات (Leads)',
    'dashboard.leads.description': 'هنا تظهر كل المحادثات (حتى اللي معملتش أوردر) - اضغط على أي صف لعرض الشات.',
    'dashboard.leads.searchPhone': '🔍 ابحث برقم العميل...',
    'dashboard.leads.searchOrder': '🔍 ابحث برقم الأوردر...',
    'dashboard.leads.clearSearch': 'مسح البحث ✕',
    'dashboard.leads.table.chatMode': 'وضع الشات',
    'dashboard.leads.table.lastUpdated': 'آخر تحديث',
    'dashboard.leads.table.lastMessage': 'آخر رسالة',
    'dashboard.leads.table.orderNumber': 'رقم الأوردر',
    'dashboard.leads.table.customerPhone': 'رقم العميل',
    'dashboard.leads.table.status': 'الحالة',
    'dashboard.leads.loading': 'جاري تحميل المحادثات...',
    'dashboard.leads.emptySearch': 'لا توجد محادثات تطابق البحث.',
    'dashboard.leads.empty': 'لا يوجد محادثات لعرضها.',
    'dashboard.leads.loadMore': '📥 تحميل المزيد من المحادثات',
    'dashboard.leads.loadingMore': '⏳ جاري التحميل...',
    'dashboard.leads.hintLoadMore': '💡 لم تجد ما تبحث عنه؟ اضغط "تحميل المزيد" لجلب سجلات أقدم',
    'feedbacks.title': '⭐ Feedbacks العملاء',
    'feedbacks.description': 'تقييمات ومشاكل العملاء بعد استلام الأوردرات - اضغط على أي صف لعرض التفاصيل والرد.',
    'feedbacks.searchPlaceholder': '🔍 ابحث برقم العميل...',
    'feedbacks.clearSearch': 'مسح البحث ✕',
    'feedbacks.table.chatMode': 'وضع الشات',
    'feedbacks.table.date': 'التاريخ',
    'feedbacks.table.status': 'الحالة',
    'feedbacks.table.comment': 'التعليق',
    'feedbacks.table.customerPhone': 'رقم العميل',
    'feedbacks.table.brandName': 'اسم البراند',
    'feedbacks.loading': 'جاري تحميل Feedbacks...',
    'feedbacks.emptySearch': 'لا توجد feedbacks تطابق البحث.',
    'feedbacks.empty': 'لا يوجد feedbacks لعرضها.',
    'feedbacks.loadMore': '📥 تحميل المزيد من التقييمات',
    'feedbacks.loadingMore': '⏳ جاري التحميل...',
    'bosta.card.title': '📦 Bosta',
    'bosta.card.active': '🟢 مفعل',
    'bosta.card.inactive': '⚪ غير مفعل',
    'bosta.card.actionsActive': 'إجراءات Bosta نشطة',
    'bosta.card.actionsStopped': 'إجراءات Bosta متوقفة',
    'bosta.card.addApiKeyFirst': '⚠️ أضف API Key أولاً',
    'bosta.card.noApiKeyAlert': '⚠️ يجب إضافة API Key أولاً من صفحة إعدادات Bosta',
    'bosta.card.priceLabel': 'سعر رسالة الـ Bosta',
    'bosta.toggle.enabled': '✅ تم تفعيل إجراءات Bosta!',
    'bosta.toggle.disabled': '❌ تم تعطيل إجراءات Bosta.',
    'bosta.table.title': '📦 شحنات Bosta',
    'bosta.table.description': 'تحديثات شحنات Bosta وحالة التوصيل - اضغط على أي صف لعرض التفاصيل والرد.',
    'bosta.table.searchPlaceholder': '🔍 ابحث برقم العميل...',
    'bosta.table.clearSearch': 'مسح البحث ✕',
    'bosta.table.chatMode': 'وضع الشات',
    'bosta.table.date': 'التاريخ',
    'bosta.table.status': 'الحالة',
    'bosta.table.comment': 'التعليق',
    'bosta.table.customerPhone': 'رقم العميل',
    'bosta.table.brandName': 'اسم البراند',
    'bosta.table.loading': 'جاري تحميل بيانات bosta...',
    'bosta.table.emptySearch': 'لا توجد سجلات bosta تطابق البحث.',
    'bosta.table.empty': 'لا يوجد سجلات bosta لعرضها.',
    'bosta.table.loadMore': '📥 تحميل المزيد من سجلات bosta',
    'bosta.table.loadingMore': '⏳ جاري التحميل...',
    'bosta.page.title': '⚙️ إعدادات Bosta',
    'bosta.page.webhookTitle': '📡 Webhook URL',
    'bosta.page.webhookDescription': 'استخدم هذا الـ Webhook URL في إعدادات Bosta الخاصة بك:',
    'bosta.page.copyLink': '📋 نسخ الرابط',
    'bosta.page.apiKeyTitle': '🔑 Bosta API Key',
    'bosta.page.apiKeyDescription': 'أدخل API Key الخاص بحساب Bosta:',
    'bosta.page.apiKeyPlaceholder': 'أدخل Bosta API Key',
    'bosta.page.saving': '⏳ جاري الحفظ...',
    'bosta.page.save': '💾 حفظ',
    'bosta.page.noteTitle': 'ℹ️ ملاحظة:',
    'bosta.page.note1': 'تأكد من تفعيل خاصية Webhooks في حساب Bosta الخاص بك',
    'bosta.page.note2': 'يجب أن تضيف الـ Webhook URL في إعدادات Bosta',
    'bosta.page.note3': 'API Key مطلوب للتواصل مع خدمة Bosta',
    'bosta.page.apiKeyRequired': '⚠️ يرجى إدخال API Key',
    'bosta.page.saveSuccess': '✅ تم حفظ API Key بنجاح!',
    'bosta.page.saveError': '❌ حدث خطأ أثناء الحفظ',
    'bosta.page.webhookCopied': '✅ تم نسخ رابط Webhook!',
    'bosta.page.clientNotFound': 'لم يتم العثور على بيانات العميل',
    'bosta.page.loadError': 'حدث خطأ في تحميل البيانات',
    'bosta.page.keyConfigured': '✅ يوجد API Key محفوظ بالفعل',
    'bosta.page.keyNotConfigured': '⚠️ لم يتم إعداد API Key بعد',
    'kb.title': 'قاعدة المعرفة (الردود الآلية)',
    'kb.loading': 'جاري تحميل قاعدة المعرفة...',
    'kb.empty': 'قاعدة المعرفة فارغة حالياً.',
    'kb.table.action': 'الإجراء',
    'kb.table.autoAnswer': 'الإجابة الآلية',
    'kb.table.question': 'السؤال المحفوظ',
    'kb.table.context': 'السياق (المنتج)',
    'kb.table.addedAt': 'تاريخ الإضافة',
    'kb.button.edit': 'تعديل ✏️',
    'kb.button.delete': 'مسح 🗑️',
    'kb.context.default': 'سؤال عام',
    'questions.title': 'أسئلة العملاء (تحتاج رد)',
    'questions.loading': 'جاري تحميل الأسئلة...',
    'questions.empty': 'لا توجد أسئلة جديدة تحتاج لرد. 10/10',
    'questions.table.action': 'الإجراء',
    'questions.table.text': 'نص السؤال',
    'questions.table.product': 'المنتج (للسياق)',
    'questions.table.order': 'رقم الأوردر',
    'questions.table.phone': 'رقم العميل',
    'questions.table.date': 'تاريخ السؤال',
    'questions.button.reply': 'رد 💬',
    'questions.button.delete': 'مسح 🗑️',
    'questions.modal.answerTitle': 'الرد على سؤال العميل',
    'questions.modal.customerQuestion': 'سؤال العميل:',
    'questions.modal.productContext': 'بخصوص منتج:',
    'questions.modal.placeholder': 'اكتب ردك هنا...',
    'questions.modal.saveToKb': 'حفظ الإجابة دي في قاعدة المعرفة (للردود الآلية مستقبلاً)',
    'questions.modal.cancel': 'إلغاء',
    'questions.modal.send': 'إرسال الإجابة 🚀',
    'questions.modal.sending': 'جاري الإرسال...',
    'questions.modal.editTitle': 'تعديل الإجابة الآلية',
    'questions.modal.savedQuestion': 'السؤال المحفوظ:',
    'questions.modal.autoAnswerLabel': 'الإجابة الآلية:',
    'questions.modal.editPlaceholder': 'اكتب الإجابة المعدلة هنا...',
    'questions.modal.saveEdit': 'حفظ التعديل ✅',
    'questions.modal.savingEdit': 'جاري الحفظ...',
    'clientInfo.title': '📋 بيانات العميل',
    'clientInfo.brandName': 'اسم البراند:',
    'clientInfo.clientId': 'معرف العميل:',
    'clientInfo.email': 'الإيميل:',
    'clientInfo.platform': 'نوع المنصة:',
    'clientInfo.shopDomain': 'دومين المتجر:',
    'clientInfo.platform.shopify': '🛍️ Shopify',
    'clientInfo.platform.easyorder': '📦 EasyOrder',
    'clientInfo.platform.default': 'عام',
    'clientInfo.shopDomain.fallback': 'غير محدد',
    'clientInfo.telegram.placeholder': 'مثال: @username أو 123456789',
    'clientInfo.telegram.getIdHint': 'احصل على User ID من البوت:',
    'clientInfo.facebook.placeholder': 'مثال: https://facebook.com/yourpage',
    'clientInfo.instagram.placeholder': 'مثال: https://instagram.com/yourpage',
    'clientInfo.close': 'إغلاق',
    'clientInfo.save': '💾 حفظ التعديلات',
    'clientInfo.saving': '⏳ جاري الحفظ...',
    'operationalSetup.banner.title': 'كمّل إعداد حسابك',
    'operationalSetup.banner.body': 'حسابك شغّال عن طريق Daf One — بس ضيف رقم تليجرام الأدمن عشان نقدر نبلغك بالأوردرات.',
    'operationalSetup.banner.cta': 'كمّل دلوقتي',
    'operationalSetup.modal.title': '🚀 كمّل إعداد حسابك',
    'operationalSetup.modal.subtitle': 'خطوة سريعة عشان نقدر نبلغك على واتساب/تليجرام.',
    'operationalSetup.modal.telegramRequired': 'رقم تليجرام (إجباري)',
    'operationalSetup.modal.save': '✅ حفظ وكمّل',
    'operationalSetup.modal.saving': '⏳ جاري الحفظ...',
    'operationalSetup.modal.close': 'بعدين',
    'operationalSetup.modal.telegramError': 'من فضلك ضيف رقم تليجرام الأول.',
    'operationalSetup.modal.saveError': 'حصل خطأ في حفظ بياناتك، حاول تاني.',
    'operationalSetup.modal.messageLanguage': 'لغة الرسايل',
    'operationalSetup.modal.messageLanguage.hint': 'اللغة اللي هنبعت بيها رسايل الواتساب لعملائك',
    'chat.modal.title': 'سجل المحادثة للعميل',
    'chat.modal.switchToManual': '👤 تحويل الشات لبشري',
    'chat.modal.switchingToManual': 'جاري التحويل...',
    'chat.modal.manualActive': '👤 الوضع البشري مفعّل — ردود الوكيل متوقفة',
    'chat.modal.modeLoading': 'جاري التأكد من وضع الشات...',
    'chat.modal.modeUnavailable': 'تعذر معرفة وضع الشات',
    'chat.modal.customerFallback': 'عميل',
    'chat.modal.loading': 'جاري تحميل سجل المحادثة...',
    'chat.modal.empty': 'لا يوجد سجل محادثة لهذا العميل.',
    'chat.modal.send': '📤 إرسال',
    'chat.modal.sending': '...',
    'chat.list.conversation': 'محادثة',
    'chat.list.emptyConversation': 'محادثة فارغة',
    'alerts.saveInfo.success': '✅ تم تحديث البيانات بنجاح!',
    'alerts.saveInfo.noChanges': '⚠️ لم يتم إدخال أي بيانات جديدة',
    'alerts.saveInfo.error': '❌ حدث خطأ أثناء التحديث',
    'alerts.answer.success': '✅ تم إرسال الرد للعميل بنجاح',
    'alerts.answer.savedToKb': '📚 تم الحفظ في قاعدة المعرفة',
    'alerts.answer.error': '❌ خطأ في إرسال الرد:',
    'alerts.question.deleteConfirm': 'هل أنت متأكد أنك تريد حذف هذا السؤال نهائياً؟ لا يمكن التراجع عن هذا الإجراء.',
    'alerts.question.deleteError': 'حدث خطأ أثناء حذف السؤال. حاول مرة أخرى.',
    'alerts.kb.saveSuccess': 'تم حفظ التعديل بنجاح!',
    'alerts.kb.saveError': 'حدث خطأ أثناء حفظ التعديل.',
    'alerts.kb.deleteConfirm': 'هل أنت متأكد من حذف هذا الرد الآلي؟ سيتم حذفه نهائياً من قاعدة المعرفة.',
    'alerts.kb.deleteError': 'حدث خطأ أثناء حذف العنصر. حاول مرة أخرى.',
    'alerts.pending.none': 'لا توجد أوردرات معلقة للتصدير',
    'alerts.export.error': 'حدث خطأ أثناء تصدير البيانات',
    'alerts.autoCancel.enabled': '✅ تم تفعيل الإلغاء التلقائي! سيتم إلغاء الأوردرات اللي مردتش على الواتساب بعد 16 ساعة من عمل الأوردر.',
    'alerts.autoCancel.disabled': '❌ تم تعطيل الإلغاء التلقائي. الأوردرات لن يتم إلغاؤها تلقائياً.',
    'alerts.duplicatedOrderNote.enabled': '✅ تم تفعيل نوته الأوردر المتكرر. هنحط نوته على الأوردر القديم والجديد لما يحصل تكرار.',
    'alerts.duplicatedOrderNote.disabled': '❌ تم تعطيل نوته الأوردر المتكرر. مش هنحط أي نوته على الأوردرات المتكررة.',
    'alerts.settingError': 'حدث خطأ أثناء تحديث الإعداد. حاول مرة أخرى.',
    'alerts.feedbackToggle.enabled': '✅ تم تفعيل طلب الـ Feedback! سيتم طلب تقييم من العملاء بعد استلام الأوردرات.',
    'alerts.feedbackToggle.disabled': '❌ تم تعطيل طلب الـ Feedback.',
    'alerts.feedback.incomplete': 'بيانات الـ Feedback غير كاملة.',
    'alerts.chat.followError': 'حدث خطأ أثناء متابعة المحادثة.',
    'alerts.chat.openError': 'حدث خطأ أثناء فتح المحادثة.',
    'alerts.chat.dataIncomplete': 'بيانات العميل غير كاملة لعرض المحادثة.',
    'alerts.chat.toggleError': 'حدث خطأ أثناء تغيير الحالة',
    'alerts.message.sentWithTime': '✅ تم إرسال الرسالة بنجاح!\n\n⏰ {time}',
    'alerts.message.sent': '✅ تم إرسال الرسالة بنجاح!',
    'alerts.message.errorPrefix': '❌',
    'alerts.message.sendError': 'حدث خطأ أثناء إرسال الرسالة',
    'alerts.toggle.incompleteData': 'بيانات غير كاملة',
    'loading.app': 'جاري التحميل...',
    'loading.shopifyCheck': 'جاري فحص حسابك في Shopify...',
    'loading.brandData': 'جاري تحميل بيانات البراند...',
    'loading.noData': 'لا يوجد بيانات...',
    'loading.magicLink': 'جاري تسجيل الدخول...',
    'loading.autoProvision': 'بنجهّز حسابك...',
    'error.clientNotFound': 'بيانات العميل غير موجودة',
    'error.fetchFailed': 'فشل تحميل بيانات العميل',
    'error.logout': 'تسجيل الخروج'
  },
};

function initialLanguage() {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  const urlLang = new URLSearchParams(window.location.search).get('lang');
  const sessionLang = sessionStorage.getItem(SESSION_KEY);
  const saved = localStorage.getItem(STORAGE_KEY);
  // Every fresh embedded open starts from Daf One, even if an older build left
  // English in localStorage or the previous Confirmation session was English.
  if (urlLang && translations[urlLang]) return urlLang;
  if (sessionLang && translations[sessionLang]) return sessionLang;
  if (saved && translations[saved]) return saved;
  return DEFAULT_LANG;
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(initialLanguage);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if (urlLang && translations[urlLang]) {
      sessionStorage.setItem(SESSION_KEY, urlLang);
      setLang(urlLang);
      return;
    }

    const sessionLang = sessionStorage.getItem(SESSION_KEY);
    if (sessionLang && translations[sessionLang]) {
      setLang(sessionLang);
      return;
    }

    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && translations[saved]) {
      setLang(saved);
      return;
    }
    localStorage.setItem(STORAGE_KEY, DEFAULT_LANG);
    sessionStorage.setItem(SESSION_KEY, DEFAULT_LANG);
    setLang(DEFAULT_LANG);
  }, []);

  const value = useMemo(() => ({
    lang,
    t: (key) => translations[lang]?.[key] || translations.en[key] || key,
    setLang: (newLang) => {
      const next = translations[newLang] ? newLang : DEFAULT_LANG;
      setLang(next);
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(SESSION_KEY, next);
        localStorage.setItem(STORAGE_KEY, next);
      }
    },
    toggleLang: () => {
      const next = lang === 'en' ? 'ar' : 'en';
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(SESSION_KEY, next);
        localStorage.setItem(STORAGE_KEY, next);
      }
      setLang(next);
    }
  }), [lang]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useI18n must be used within LanguageProvider');
  return ctx;
}
